import React, { useEffect, useState } from "react";
import { timeLogsAPI } from "../../api/client";
import { formatDuration } from "../../utils/formatDuration";

export default function ProjectTimeSummaryTab({ projectId, projectName }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!projectId) return;
      setLoading(true);
      setError("");
      try {
        const resp = await timeLogsAPI.projectSummary(projectId);
        setSummary(resp.data);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load time summary");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [projectId]);

  const downloadReport = async () => {
    setDownloading(true);
    setError("");
    try {
      const resp = await timeLogsAPI.projectReport(projectId);
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const filename =
        resp.headers?.["content-disposition"]
          ?.split("filename=")?.[1]
          ?.replace(/"/g, "") || `${projectName || "project"}-time-summary.pdf`;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to download report");
    } finally {
      setDownloading(false);
    }
  };

  const allocatedHours = Number(summary?.allocatedHours || 0);
  const loggedHours = Number(summary?.loggedHours || 0);
  const variance = Number(summary?.variance || 0);
  const additionalWorkHours = Number(summary?.additionalWorkHours || 0);
  const utilizationPercent = allocatedHours
    ? Math.round((loggedHours / allocatedHours) * 100)
    : 0;
  const taskHours = Number(summary?.taskHours || 0);
  const byTask = Array.isArray(summary?.byTask) ? summary.byTask : [];
  const byUser = Array.isArray(summary?.byUser) ? summary.byUser : [];
  const totalLoggedByUsers = byUser.reduce(
    (sum, user) => sum + Number(user.totalHours || 0),
    0,
  );

  if (loading)
    return <div className="small-muted">Loading time summary...</div>;

  return (
    <div>
      <div className="modal-grid" style={{ marginBottom: 12 }}>
        <div className="panel">
          <div className="small-muted">Allocated Time (Planned)</div>
          <div style={{ fontWeight: 700, fontSize: 18 }}>
            {allocatedHours.toFixed(2)}h
          </div>
        </div>

        <div className="panel">
          <div className="small-muted">Logged Time (Actual)</div>
          <div style={{ fontWeight: 700, fontSize: 18 }}>
            {loggedHours.toFixed(2)}h
          </div>
          <div className="small-muted">
            {formatDuration(Math.round(loggedHours * 60))}
          </div>
          {additionalWorkHours > 0 && (
            <div className="small-muted">
              incl. {formatDuration(Math.round(additionalWorkHours * 60))}{" "}
              additional work after completion
            </div>
          )}
        </div>

        <div className="panel">
          <div className="small-muted">Variance (Actual - Planned)</div>
          <div
            style={{
              fontWeight: 700,
              fontSize: 18,
              color: variance > 0 ? "#b91c1c" : "#15803d",
            }}
          >
            {variance > 0 ? "+" : ""}
            {variance.toFixed(2)}h
          </div>
        </div>

        <div className="panel">
          <div className="small-muted">Task Time</div>
          <div style={{ fontWeight: 700, fontSize: 18 }}>
            {taskHours.toFixed(2)}h
          </div>
          <div className="small-muted">
            Logged on tasks · not part of variance
          </div>
        </div>
      </div>

      <div className="panel" style={{ marginBottom: 12 }}>
        <h4 style={{ marginTop: 0, marginBottom: 8 }}>Utilization</h4>
        <div className="small-muted" style={{ marginBottom: 8 }}>
          {utilizationPercent}% of planned hours consumed
        </div>
        <div
          style={{
            height: 10,
            borderRadius: 999,
            background: "#e5e7eb",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${Math.min(utilizationPercent, 100)}%`,
              height: "100%",
              background:
                utilizationPercent > 100
                  ? "#dc2626"
                  : utilizationPercent > 80
                    ? "#f59e0b"
                    : "#16a34a",
            }}
          />
        </div>
      </div>

      {/* <div className="panel">
        <h4 style={{ marginTop: 0 }}>Stage Breakdown</h4>
        <div className="stage-assign-row" style={{ fontWeight: 700 }}>
          <div style={{ width: 200 }}>Stage</div>
          <div style={{ width: 120 }}>Planned</div>
          <div style={{ width: 120 }}>Actual</div>
          <div>Assignee</div>
        </div>
        {(summary?.byStage || []).map((stage) => (
          <div key={stage.stageName} className="stage-assign-row">
            <div style={{ width: 200 }}>{stage.stageName}</div>
            <div className="small-muted" style={{ width: 120 }}>
              {Number(stage.allocatedHours || 0).toFixed(2)}h
            </div>
            <div className="small-muted" style={{ width: 120 }}>
              {Number(stage.loggedHours || 0).toFixed(2)}h
            </div>
            <div className="small-muted">{stage.assigneeName}</div>
          </div>
        ))}
      </div> */}

      <div className="panel" style={{ marginTop: 12 }}>
        <h4 style={{ marginTop: 0 }}>User Contribution</h4>
        <div className="small-muted" style={{ marginBottom: 8 }}>
          % of project is against planned hours. % of logged is share of actual
          logged time. Task time is logged on tasks and isn't part of either.
        </div>
        <div className="time-summary-table-wrap">
          <table className="time-summary-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Actual</th>
                <th>% of project</th>
                <th>% of logged</th>
                <th>Task time</th>
              </tr>
            </thead>
            <tbody>
              {byUser.map((user) => (
                <tr key={user.userId}>
                  <td className="time-summary-name">{user.name}</td>
                  <td>
                    {Number(user.totalHours || 0).toFixed(2)}h
                    {user.additionalWorkHours > 0 && (
                      <div className="small-muted">
                        incl. {Number(user.additionalWorkHours).toFixed(2)}h
                        additional
                      </div>
                    )}
                  </td>
                  <td>{user.percentageOfProject}%</td>
                  <td>
                    {totalLoggedByUsers
                      ? `${Math.round((Number(user.totalHours || 0) / totalLoggedByUsers) * 100)}%`
                      : "0%"}
                  </td>
                  <td>
                    {user.taskHours > 0
                      ? `${Number(user.taskHours).toFixed(2)}h`
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="panel" style={{ marginTop: 12 }}>
        <h4 style={{ marginTop: 0 }}>Tasks</h4>
        <div className="small-muted" style={{ marginBottom: 8 }}>
          Every task attached to this project, in section order, and the time
          logged on it.
        </div>
        {byTask.length === 0 ? (
          <div className="small-muted">
            No tasks are attached to this project.
          </div>
        ) : (
          <div className="time-summary-table-wrap">
            <table className="time-summary-table">
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Section</th>
                  <th>Assignee</th>
                  <th>Status</th>
                  <th>Logged</th>
                </tr>
              </thead>
              <tbody>
                {byTask.map((task) => (
                  <tr key={task.taskId}>
                    <td
                      className={`time-summary-name${task.depth ? " is-subtask" : ""}`}
                      style={
                        task.depth
                          ? { paddingLeft: 12 + Math.min(task.depth, 4) * 16 }
                          : undefined
                      }
                    >
                      {task.depth > 0 && (
                        <span className="time-summary-subtask-mark">↳ </span>
                      )}
                      {task.name}
                      {task.type !== "task" && (
                        <span className="time-summary-type">
                          {task.type === "milestone" ? "Milestone" : "Approval"}
                        </span>
                      )}
                    </td>
                    <td>{task.sectionName || "No section"}</td>
                    <td>{task.assigneeName || "Unassigned"}</td>
                    <td>
                      <span
                        className={`time-summary-status ${
                          task.statusLabel === "Rejected"
                            ? "bad"
                            : task.completed
                              ? "good"
                              : "open"
                        }`}
                      >
                        {task.statusLabel}
                      </span>
                    </td>
                    <td>
                      {task.loggedHours > 0
                        ? formatDuration(Math.round(task.loggedHours * 60))
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ marginTop: 12 }}>
        <button
          className="btn-secondary"
          disabled={downloading}
          onClick={downloadReport}
        >
          {downloading ? "Preparing..." : "Download Time Report"}
        </button>
      </div>

      {error && (
        <div className="small-muted" style={{ color: "#b91c1c" }}>
          {error}
        </div>
      )}
    </div>
  );
}
